import React from 'react';
import { Database, Key, Bell, Globe, AlertTriangle } from 'lucide-react';

const SettingSection: React.FC<{
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, description, icon: Icon, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <div className="flex items-center mb-4">
      <div className="flex-shrink-0">
        <Icon className="h-6 w-6 text-slate-500" />
      </div>
      <div className="ml-3">
        <h3 className="text-lg font-medium text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

const EmptyConfigMessage: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
    <div className="flex items-center">
      <AlertTriangle className="h-5 w-5 text-slate-400 mr-2" />
      <span className="text-slate-600 text-sm">{message}</span>
    </div>
  </div>
);

export const SettingsPage: React.FC = () => {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Configure system settings and integrations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* API Configuration */}
        <SettingSection
          title="API Configuration"
          description="Configure backend API endpoints and authentication"
          icon={Database}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                API Endpoint
              </label>
              <input
                type="url"
                placeholder="https://api.collectability.ai"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled
              />
              <p className="text-xs text-slate-500 mt-1">
                Configure in environment variables (VITE_AWS_API_ENDPOINT)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                API Key Status
              </label>
              <EmptyConfigMessage message="API key configuration not available in UI for security" />
            </div>
          </div>
        </SettingSection>

        {/* Authentication */}
        <SettingSection
          title="Authentication"
          description="Manage authentication and access control"
          icon={Key}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Admin Users
              </label>
              <EmptyConfigMessage message="User management requires backend integration" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Session Settings
              </label>
              <EmptyConfigMessage message="Session configuration managed by backend" />
            </div>
          </div>
        </SettingSection>

        {/* Notifications */}
        <SettingSection
          title="Notifications"
          description="Configure email and system notifications"
          icon={Bell}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900">Batch Completion Emails</h4>
                <p className="text-xs text-slate-600">Send email when batch processing completes</p>
              </div>
              <button className="bg-slate-200 relative inline-flex h-6 w-11 items-center rounded-full">
                <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition"></span>
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900">Error Notifications</h4>
                <p className="text-xs text-slate-600">Alert administrators of system errors</p>
              </div>
              <button className="bg-slate-200 relative inline-flex h-6 w-11 items-center rounded-full">
                <span className="translate-x-1 inline-block h-4 w-4 transform rounded-full bg-white transition"></span>
              </button>
            </div>
            <EmptyConfigMessage message="Notification settings require backend configuration" />
          </div>
        </SettingSection>

        {/* System */}
        <SettingSection
          title="System"
          description="General system configuration and maintenance"
          icon={Globe}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                System Status
              </label>
              <div className="flex items-center">
                <div className="h-3 w-3 bg-green-400 rounded-full mr-2"></div>
                <span className="text-sm text-slate-600">Admin Portal Online</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Version Information
              </label>
              <div className="text-sm text-slate-600 space-y-1">
                <div>Admin Portal: v1.0.0</div>
                <div>Backend API: No data available</div>
                <div>Last Updated: {new Date().toLocaleDateString()}</div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Maintenance
              </label>
              <div className="flex gap-2">
                <button className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors">
                  Clear Cache
                </button>
                <button className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition-colors">
                  Export Logs
                </button>
              </div>
            </div>
          </div>
        </SettingSection>
      </div>

      {/* Save Changes */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Configuration Changes</h3>
            <p className="text-sm text-slate-600">
              Most settings require backend API configuration and cannot be modified from this interface.
            </p>
          </div>
          <button 
            className="px-4 py-2 bg-slate-400 text-white rounded-lg font-medium cursor-not-allowed"
            disabled
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
